/**
 * docSedReverseIndex — extracted helper.
 *
 * Builds the reverse index `docId → SED section ids this doc contributes
 * to` by walking every section's `linkedDocumentIds` (legacy) +
 * `supportingDocIds` (Documents_On_Site Prompt 11) union. Pure;
 * deterministic; safe to call on every render.
 *
 * History: shipped originally as `buildReverseIndex` exported from
 * `DocumentContentsPanel.tsx` (KSD4) and re-exported by SET-D5 so the
 * Site Evidence tab + DocViewerDialog popover could share one derivation.
 * EDX-1 deletes DocumentContentsPanel along with the other three SET-M2
 * doc-list panels; this module preserves the helper so SiteLandingPage
 * + DocSedIngestionPopover keep working without touching their call
 * sites beyond an import path swap.
 *
 * No behaviour change vs. the inline KSD4 version — same union, same
 * order-preserving accumulator. The existing
 * `DocumentContentsPanel.test.tsx` reverse-index assertions are
 * superseded by the DocSedIngestionPopover tests which exercise the
 * same projection through the popover.
 */
import type { SiteEvidenceDocument, SedSectionId } from '../types/siteEvidence';
import { SED_SECTION_IDS } from '../types/siteEvidence';

export function buildReverseIndex(sed: SiteEvidenceDocument): Map<string, SedSectionId[]> {
  const out = new Map<string, SedSectionId[]>();
  for (const sid of SED_SECTION_IDS) {
    const slot = (sed as unknown as Record<string, { linkedDocumentIds?: string[]; supportingDocIds?: string[] } | undefined>)[sid];
    if (!slot) continue;
    const union = new Set<string>([
      ...(slot.linkedDocumentIds ?? []),
      ...(slot.supportingDocIds ?? []),
    ]);
    for (const docId of union) {
      const list = out.get(docId) ?? [];
      if (!list.includes(sid)) list.push(sid);
      out.set(docId, list);
    }
  }
  return out;
}
